import React from 'react';
import type { ApiInvitation, ApiTemplate } from '@/src/types';
import { JsonTemplateRenderer } from './JsonTemplateRenderer';
import { TemplateRenderer, registry } from '@/src/modules/templates';
import { SceneExperienceRenderer } from '@/src/modules/experiences/scene/SceneExperienceRenderer';
import { resolveExperienceManifest } from '@/src/modules/pages/ExperiencePreview';

/**
 * Build a rich sample invitation so code-rendered templates have realistic
 * data to display in the card thumbnail. Wedding templates get full
 * customData so couple-name components render correctly.
 */
function sampleInvitation(template: ApiTemplate): ApiInvitation {
  const eventDate = new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString();
  return {
    id: `preview-${template.id}`,
    publicSlug: null,
    templateId: template.id,
    occasionType: template.occasionType,
    status: 'PUBLISHED',
    hostedUrl: null,
    eventName: template.occasionType === 'WEDDING' ? 'Aarav & Mira' : 'Your Celebration',
    eventDate,
    eventVenue: 'The Grand Venue, Mumbai',
    hostName: 'The Host Family',
    customMessage: 'Join us for an evening of joy, stories, and celebration.',
    rsvpLink: '#',
    mediaUrls: [],
    customData:
      template.occasionType === 'WEDDING'
        ? {
            wedding: {
              groom: {
                fullName: 'Aarav Sharma',
                nickname: 'Aarav',
                profession: 'Architect',
                fatherName: 'Amit Sharma',
                motherName: 'Neelam Sharma',
                about: 'Thoughtful, warm, and happiest when surrounded by family and music.',
              },
              bride: {
                fullName: 'Mira Mehta',
                nickname: 'Mira',
                profession: 'Designer',
                fatherName: 'Sanjay Mehta',
                motherName: 'Kavita Mehta',
                about: 'Graceful, curious, and known for making every gathering feel intimate.',
              },
              weddingEvents: [
                {
                  title: 'Mehendi',
                  date: new Date(Date.now() + 43 * 24 * 60 * 60 * 1000).toISOString(),
                  startTime: '05:00 PM',
                  venue: 'Courtyard Garden',
                  description: 'An evening of henna, music, and family laughter.',
                },
                {
                  title: 'Wedding',
                  date: eventDate,
                  startTime: '07:30 PM',
                  venue: 'The Grand Venue, Mumbai',
                  description: 'The ceremony, blessings, dinner, and celebration.',
                },
              ],
            },
          }
        : undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Renders a card-sized thumbnail preview for a template.
 *
 * Priority order:
 * 1. Registry-backed code templates → live scaled-down TemplateRenderer.
 *    CSS `transform: scale()` creates a new containing block for
 *    `position:fixed` descendants (e.g. Lagna Patrika's opening gate),
 *    so fixed overlays are correctly contained within the card bounds.
 * 2. Experience / AI-native templates → live scaled-down SceneExperienceRenderer.
 * 3. JSON-schema templates → live scaled-down JsonTemplateRenderer.
 * 4. API-served templates with a previewImageUrl → static image overlay card.
 * 5. Generic labelled fallback.
 */
export function TemplateCardPreview({ template }: { template: ApiTemplate }) {
  // --- 1. Live preview for all registry-backed code templates ---
  if (registry.has(template.id)) {
    return (
      <div className="origin-top-left scale-[0.34] w-[294%] h-[294%] pointer-events-none overflow-hidden">
        <TemplateRenderer templateId={template.id} data={sampleInvitation(template)} />
      </div>
    );
  }

  // --- 2. Experience / AI-native templates ---
  if (
    template.renderMode === 'experience' ||
    template.id.startsWith('exp-') ||
    (template.capabilities as any)?.experience === true
  ) {
    try {
      const manifest = resolveExperienceManifest(template.id, sampleInvitation(template));
      if (manifest) {
        return (
          <div className="origin-top-left scale-[0.34] w-[294%] h-[294%] pointer-events-none overflow-hidden">
            <SceneExperienceRenderer manifest={manifest} preview />
          </div>
        );
      }
    } catch (e) {
      // fallback
    }
  }

  // --- 3. JSON-schema templates ---
  if (template.renderMode === 'json-renderer' && template.layoutSchema) {
    return (
      <div className="origin-top-left scale-[0.34] w-[294%] h-[294%] pointer-events-none overflow-hidden">
        <JsonTemplateRenderer
          schema={template.layoutSchema}
          theme={template.themeConfig}
          data={sampleInvitation(template)}
          mode="preview"
        />
      </div>
    );
  }

  // --- 3. API-served templates with a static preview image ---
  if (template.previewImageUrl) {
    return (
      <div className="relative h-full w-full overflow-hidden bg-[#11130f] text-[#fffaf0]">
        <img
          src={template.previewImageUrl}
          alt={template.name}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 text-[#faf6ef]">
          <p className="text-[0.56rem] font-semibold uppercase tracking-[0.3em] text-[#ecdcc1]">
            {template.occasionType.replace(/_/g, ' ')}
          </p>
          <h3 className="mt-2 font-serif text-[1.5rem] font-bold leading-tight">{template.name}</h3>
        </div>
      </div>
    );
  }

  // --- 4. Generic labelled fallback ---
  return (
    <div
      className="w-full h-full flex items-center justify-center text-center px-8"
      style={{ background: 'linear-gradient(135deg, #fcf8ff, #e8d5c4)', color: '#574eb1' }}
    >
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-bold">
          {template.occasionType.replace(/_/g, ' ')}
        </p>
        <p className="mt-3 text-2xl font-bold leading-tight">{template.name}</p>
      </div>
    </div>
  );
}
